import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../context/ConfigContext';
import api from '../../api';

const defaultTemplates = [
  {
    id: 'welcome',
    name: 'Welcome Email',
    description: 'Sent when a new customer registers',
    subject: 'Welcome to {{companyName}}!',
    body: `Dear {{customerName}},

Welcome to {{companyName}}! We're excited to have you join us.

Your account has been successfully created. You can now browse available properties, apply for financing, and manage your loans through our customer portal.

If you have any questions, please don't hesitate to reach out to us at {{companyEmail}} or {{companyPhone}}.

Best regards,
The {{companyName}} Team`
  },
  {
    id: 'payment-reminder',
    name: 'Payment Reminder',
    description: 'Sent before payment is due',
    subject: 'Payment Reminder - {{amountDue}} Due on {{dueDate}}',
    body: `Dear {{customerName}},

This is a friendly reminder that your upcoming payment is due soon.

Property: {{propertyAddress}}
Amount Due: {{amountDue}}
Due Date: {{dueDate}}

To make your payment, please log in to your customer portal or contact us directly.

Thank you for your continued partnership.

Best regards,
The {{companyName}} Team`
  },
  {
    id: 'payment-confirmation',
    name: 'Payment Confirmation',
    description: 'Sent when payment is received',
    subject: 'Payment Received - Thank You!',
    body: `Dear {{customerName}},

Thank you! We have received your payment.

Property: {{propertyAddress}}
Payment Amount: {{paymentAmount}}
Payment Date: {{paymentDate}}
Remaining Balance: {{remainingBalance}}

A receipt has been added to your account. You can view your complete payment history in your customer portal.

Thank you for your payment.

Best regards,
The {{companyName}} Team`
  },
  {
    id: 'late-payment',
    name: 'Late Payment Notice',
    description: 'Sent when payment is overdue',
    subject: 'Important: Payment Past Due',
    body: `Dear {{customerName}},

Our records indicate that your payment is past due.

Property: {{propertyAddress}}
Amount Past Due: {{amountDue}}
Original Due Date: {{dueDate}}
Days Overdue: {{daysOverdue}}

Please make your payment as soon as possible to avoid any late fees or penalties. If you're experiencing financial difficulties, please contact us immediately at {{companyPhone}} to discuss payment options.

Best regards,
The {{companyName}} Team`
  },
  {
    id: 'loan-payoff',
    name: 'Loan Payoff Confirmation',
    description: 'Sent when loan is fully paid',
    subject: 'Congratulations! Your Loan is Paid in Full',
    body: `Dear {{customerName}},

Congratulations! We are pleased to confirm that your loan has been paid in full.

Property: {{propertyAddress}}
Final Payment Amount: {{paymentAmount}}
Payoff Date: {{paymentDate}}

Your deed transfer documents will be prepared and sent to you within 30 days. If you have any questions about the deed transfer process, please contact us.

Thank you for choosing {{companyName}}. We wish you all the best!

Best regards,
The {{companyName}} Team`
  }
];

const placeholders = [
  { tag: '{{customerName}}', description: 'Customer full name' },
  { tag: '{{companyName}}', description: 'Your company name' },
  { tag: '{{companyEmail}}', description: 'Company email address' },
  { tag: '{{companyPhone}}', description: 'Company phone number' },
  { tag: '{{propertyAddress}}', description: 'Property location/address' },
  { tag: '{{amountDue}}', description: 'Amount due for payment' },
  { tag: '{{dueDate}}', description: 'Payment due date' },
  { tag: '{{paymentAmount}}', description: 'Amount paid' },
  { tag: '{{paymentDate}}', description: 'Date of payment' },
  { tag: '{{remainingBalance}}', description: 'Remaining loan balance' },
  { tag: '{{daysOverdue}}', description: 'Days payment is late' }
];

const sampleData = {
  customerName: 'John Smith',
  companyName: 'DeedStack Properties',
  companyEmail: 'support@deedstack.com',
  companyPhone: '(555) 123-4567',
  propertyAddress: '123 Oak Lane, Austin, TX',
  amountDue: '$450.00',
  dueDate: 'January 15, 2025',
  paymentAmount: '$450.00',
  paymentDate: 'January 14, 2025',
  remainingBalance: '$12,500.00',
  daysOverdue: '5'
};

function EmailTemplatesSettings() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const demoStyle = demoDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {};
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Payment receipts: a real, server-backed, per-tenant setting. Unlike the
  // template editor below (which is localStorage only), this one governs
  // whether customers of this tenant are emailed a receipt after every payment.
  const [receiptsEnabled, setReceiptsEnabled] = useState(true);
  const [receiptsLoaded, setReceiptsLoaded] = useState(false);
  const [receiptsSaving, setReceiptsSaving] = useState(false);
  const [receiptsMessage, setReceiptsMessage] = useState('');

  useEffect(() => {
    // Load templates from localStorage or use defaults
    const saved = localStorage.getItem('deedstack_email_templates');
    if (saved) {
      setTemplates(JSON.parse(saved));
    } else {
      setTemplates(defaultTemplates);
    }
  }, []);

  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await api.get('/admin/settings/email', {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Default ON, matching the server: absent means enabled.
        setReceiptsEnabled(response.data.paymentReceiptsEnabled !== false);
      } catch (error) {
        console.error('Failed to load email settings:', error);
      } finally {
        setReceiptsLoaded(true);
      }
    };
    loadEmailSettings();
  }, []);

  const handleToggleReceipts = async () => {
    if (demoDisabled || receiptsSaving) return;
    const next = !receiptsEnabled;

    setReceiptsSaving(true);
    setReceiptsMessage('');
    try {
      const token = localStorage.getItem('adminToken');
      await api.patch('/admin/settings/email', { paymentReceiptsEnabled: next }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceiptsEnabled(next);
      setReceiptsMessage(next ? 'Receipts turned on.' : 'Receipts turned off.');
      setTimeout(() => setReceiptsMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save email settings:', error);
      setReceiptsMessage('Could not save that. Please try again.');
    } finally {
      setReceiptsSaving(false);
    }
  };

  useEffect(() => {
    if (selectedTemplate) {
      setEditSubject(selectedTemplate.subject);
      setEditBody(selectedTemplate.body);
      setHasChanges(false);
    }
  }, [selectedTemplate]);

  const handleSelectTemplate = (template) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedTemplate(template);
    setSaveMessage('');
  };

  const handleSubjectChange = (e) => {
    setEditSubject(e.target.value);
    setHasChanges(true);
    setSaveMessage('');
  };

  const handleBodyChange = (e) => {
    setEditBody(e.target.value);
    setHasChanges(true);
    setSaveMessage('');
  };

  const handleInsertPlaceholder = (tag) => {
    // Insert at cursor position in body textarea
    const textarea = document.getElementById('template-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = editBody.substring(0, start) + tag + editBody.substring(end);
      setEditBody(newBody);
      setHasChanges(true);
      setSaveMessage('');
      // Focus and set cursor after inserted tag
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    }
  };

  const handleSave = () => {
    if (!selectedTemplate) return;

    const updatedTemplates = templates.map(t =>
      t.id === selectedTemplate.id
        ? { ...t, subject: editSubject, body: editBody }
        : t
    );

    setTemplates(updatedTemplates);
    localStorage.setItem('deedstack_email_templates', JSON.stringify(updatedTemplates));
    setSelectedTemplate({ ...selectedTemplate, subject: editSubject, body: editBody });
    setHasChanges(false);
    setSaveMessage('Template saved successfully!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleReset = () => {
    if (!selectedTemplate) return;

    if (window.confirm('Reset this template to its default content? This cannot be undone.')) {
      const defaultTemplate = defaultTemplates.find(t => t.id === selectedTemplate.id);
      if (defaultTemplate) {
        setEditSubject(defaultTemplate.subject);
        setEditBody(defaultTemplate.body);

        const updatedTemplates = templates.map(t =>
          t.id === selectedTemplate.id ? defaultTemplate : t
        );
        setTemplates(updatedTemplates);
        localStorage.setItem('deedstack_email_templates', JSON.stringify(updatedTemplates));
        setSelectedTemplate(defaultTemplate);
        setHasChanges(false);
        setSaveMessage('Template reset to default!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    }
  };

  const renderPreviewContent = (content) => {
    let result = content;
    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, value);
    });
    return result;
  };

  return (
    <div className="admin-dashboard-container">
      {/* Header with Back Button */}
      <div className="admin-hub-header">
        <button
          onClick={() => navigate('/admin/settings-hub')}
          className="admin-hub-back-btn"
        >
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Email Templates</h1>
        <p className="admin-hub-subtitle">Customize automated email messages sent to customers</p>
      </div>

      {/* Payment receipts toggle */}
      <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '20px', flexWrap: 'wrap'
        }}>
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <h3 style={{ margin: '0 0 6px 0', fontSize: '17px' }}>Payment receipts</h3>
            <p style={{ margin: 0, color: 'var(--color-text-light)', fontSize: '14px', lineHeight: 1.5 }}>
              Email the customer an itemized receipt after every payment &mdash; both
              payments made through the customer portal and payments you record by hand.
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button
              type="button"
              className={receiptsEnabled ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={handleToggleReceipts}
              disabled={demoDisabled || receiptsSaving || !receiptsLoaded}
              style={demoStyle}
            >
              {!receiptsLoaded
                ? 'Loading…'
                : receiptsSaving
                  ? 'Saving…'
                  : receiptsEnabled ? 'On' : 'Off'}
            </button>
            {receiptsMessage && (
              <p style={{ margin: '8px 0 0 0', color: 'var(--color-text-light)', fontSize: '13px' }}>
                {receiptsMessage}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="email-templates-layout">
        {/* Template List */}
        <div className="email-templates-sidebar">
          <h3 className="email-templates-sidebar-title">Templates</h3>
          <div className="email-templates-list">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`email-template-item ${selectedTemplate?.id === template.id ? 'email-template-item-active' : ''}`}
                onClick={() => handleSelectTemplate(template)}
              >
                <div className="email-template-item-name">{template.name}</div>
                <div className="email-template-item-desc">{template.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Editor Area */}
        <div className="email-templates-editor">
          {selectedTemplate ? (
            <>
              <div className="email-editor-header">
                <h3>{selectedTemplate.name}</h3>
                <div className="email-editor-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowPreview(true)}
                  >
                    Preview
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => !demoDisabled && handleReset()}
                    disabled={demoDisabled}
                    style={demoStyle}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    Reset to Default
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => !demoDisabled && handleSave()}
                    disabled={!hasChanges || demoDisabled}
                    style={(demoDisabled || !hasChanges) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    Save Changes
                  </button>
                </div>
              </div>

              {saveMessage && (
                <div className="email-save-message">{saveMessage}</div>
              )}

              <div className="email-editor-form">
                <div className="email-editor-field">
                  <label htmlFor="template-subject">Subject Line</label>
                  <input
                    type="text"
                    id="template-subject"
                    value={editSubject}
                    onChange={handleSubjectChange}
                    className="email-editor-input"
                    placeholder="Email subject..."
                  />
                </div>

                <div className="email-editor-field">
                  <label htmlFor="template-body">Email Body</label>
                  <textarea
                    id="template-body"
                    value={editBody}
                    onChange={handleBodyChange}
                    className="email-editor-textarea"
                    rows="15"
                    placeholder="Email content..."
                  />
                </div>
              </div>

              {/* Placeholders Panel */}
              <div className="email-placeholders-panel">
                <h4>Available Placeholders</h4>
                <p className="email-placeholders-hint">Click to insert at cursor position</p>
                <div className="email-placeholders-grid">
                  {placeholders.map((p) => (
                    <button
                      key={p.tag}
                      className="email-placeholder-btn"
                      onClick={() => handleInsertPlaceholder(p.tag)}
                      title={p.description}
                    >
                      {p.tag}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="email-editor-empty">
              <div className="email-editor-empty-icon">📧</div>
              <p>Select a template from the list to edit</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && selectedTemplate && (
        <div className="email-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="email-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-preview-header">
              <h3>Email Preview</h3>
              <button
                className="email-preview-close"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
            </div>
            <div className="email-preview-content">
              <div className="email-preview-subject">
                <strong>Subject:</strong> {renderPreviewContent(editSubject)}
              </div>
              <div className="email-preview-body">
                {renderPreviewContent(editBody).split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="email-preview-footer">
              <p className="email-preview-note">
                This preview uses sample data. Actual emails will use real customer information.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setShowPreview(false)}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplatesSettings;
